import { gql } from "@apollo/client";

const listMaterialsWithInventory = /* GraphQL */ `
  query MaterialsByDefiner(
    $definerID: ID!
    $filter: ModelMaterialFilterInput
    $limit: Int
    $nextToken: String
  ) {
    MaterialsByDefiner(
      definerID: $definerID
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        mpn
        mfr
        inventory
      }
      nextToken
    }
  }
`;

const listMaterialEntityRelations = /* GraphQL */ `
  query MaterialEntityRelationsByDefiner(
    $definerID: ID!
    $limit: Int
    $nextToken: String
  ) {
    MaterialEntityRelationsByDefiner(
      definerID: $definerID
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        materialID
        code
      }
      nextToken
    }
  }
`;

const listActivePOs = /* GraphQL */ `
  query POByDefinerID(
    $definerID: ID!
    $openQty: ModelFloatKeyConditionInput
    $filter: ModelPOFilterInput
    $limit: Int
    $nextToken: String
  ) {
    POByDefinerID(
      definerID: $definerID
      openQty: $openQty
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        number
        itemNumber
        qty
        openQty
        statusCode
        piID
        customerID
        vendorID
        materialID
        materialEntityRelationID
        CRD
        materialEntityRelation {
          id
          code
        }
        material {
          id
          mpn
          mfr
          inventory
        }
      }
      nextToken
      __typename
    }
  }
`;

async function fetchAllPOs({ client, definerID, filter }) {
  const all = [];
  let nextToken = null;

  do {
    const { data } = await client.query({
      query: gql(listActivePOs),
      variables: { definerID, openQty: { gt: 0 }, filter, limit: 1000, nextToken },
      fetchPolicy: "network-only",
    });
    const items = data?.POByDefinerID?.items || [];
    all.push(...items);
    nextToken = data?.POByDefinerID?.nextToken;
  } while (nextToken);

  return all;
}

/**
 * Compares SPO open quantities + current inventory against CPO open quantities,
 * grouped by material code. Codes with multiple approved materials show combined
 * totals with per-material breakdown.
 */
export default async function analyzeSupplyDemand({ client, definerID, entityIDs }) {
  const makeFilter = (idField, statusFilter) => ({
    and: [
      { or: entityIDs.map((id) => ({ [idField]: { eq: id } })) },
      statusFilter,
    ],
  });

  // Fetch all MaterialEntityRelations to build code <-> materialID mappings
  const allRelations = [];
  let relNextToken = null;
  do {
    const { data } = await client.query({
      query: gql(listMaterialEntityRelations),
      variables: { definerID, limit: 1000, nextToken: relNextToken },
      fetchPolicy: "network-only",
    });
    allRelations.push(...(data?.MaterialEntityRelationsByDefiner?.items || []));
    relNextToken = data?.MaterialEntityRelationsByDefiner?.nextToken;
  } while (relNextToken);

  const codesByMaterialID = {};
  for (const r of allRelations) {
    if (!r.materialID || !r.code) continue;
    if (!codesByMaterialID[r.materialID]) codesByMaterialID[r.materialID] = new Set();
    codesByMaterialID[r.materialID].add(r.code);
  }

  // Fetch materials with non-zero inventory
  const inventoryMaterials = [];
  let invNextToken = null;
  do {
    const { data } = await client.query({
      query: gql(listMaterialsWithInventory),
      variables: {
        definerID,
        filter: { or: [{ inventory: { gt: 0 } }, { inventory: { lt: 0 } }] },
        limit: 1000,
        nextToken: invNextToken,
      },
      fetchPolicy: "network-only",
    });
    inventoryMaterials.push(...(data?.MaterialsByDefiner?.items || []));
    invNextToken = data?.MaterialsByDefiner?.nextToken;
  } while (invNextToken);

  const inventoryByMaterialID = {};
  for (const mat of inventoryMaterials) {
    inventoryByMaterialID[mat.id] = parseFloat(mat.inventory) || 0;
  }

  // Fetch CPOs and SPOs
  const cpos = await fetchAllPOs({
    client,
    definerID,
    filter: makeFilter("vendorID", { statusCode: { ne: "900" } }),
  });

  const spos = await fetchAllPOs({
    client,
    definerID,
    filter: makeFilter("customerID", {
      and: [
        { statusCode: { ne: "900" } },
        { statusCode: { ne: "400" } },
        { piID: { attributeExists: false } },
      ],
    }),
  });

  // Aggregate by code (fallback to materialID for POs without a code)
  // codeMap[key] = { code, materialMap: Map<materialID, matEntry>, cpoDemand, spoSupply }
  const codeMap = {};

  const getOrCreateGroup = (code, material) => {
    const key = code || `mat:${material?.id}`;
    if (!codeMap[key]) {
      codeMap[key] = {
        code: code || "",
        materialMap: new Map(),
        cpoDemand: 0,
        spoSupply: 0,
      };
    }
    if (material?.id && !codeMap[key].materialMap.has(material.id)) {
      codeMap[key].materialMap.set(material.id, {
        materialID: material.id,
        mpn: material.mpn || "",
        mfr: material.mfr || "",
        inventory: parseFloat(material.inventory) || 0,
        cpoDemand: 0,
        spoSupply: 0,
      });
    }
    return codeMap[key];
  };

  for (const po of cpos) {
    const code = po.materialEntityRelation?.code;
    const group = getOrCreateGroup(code, po.material);
    const qty = parseFloat(po.openQty) || 0;
    group.cpoDemand += qty;
    if (po.material?.id) {
      const matEntry = group.materialMap.get(po.material.id);
      if (matEntry) matEntry.cpoDemand += qty;
    }
  }

  for (const po of spos) {
    const code = po.materialEntityRelation?.code;
    const group = getOrCreateGroup(code, po.material);
    const qty = parseFloat(po.openQty) || 0;
    group.spoSupply += qty;
    if (po.material?.id) {
      const matEntry = group.materialMap.get(po.material.id);
      if (matEntry) matEntry.spoSupply += qty;
    }
  }

  // Merge inventory-only materials (no open POs)
  for (const mat of inventoryMaterials) {
    const codes = codesByMaterialID[mat.id];
    if (codes && codes.size > 0) {
      for (const code of codes) {
        if (!codeMap[code]) {
          getOrCreateGroup(code, mat);
        } else if (!codeMap[code].materialMap.has(mat.id)) {
          codeMap[code].materialMap.set(mat.id, {
            materialID: mat.id,
            mpn: mat.mpn || "",
            mfr: mat.mfr || "",
            inventory: parseFloat(mat.inventory) || 0,
            cpoDemand: 0,
            spoSupply: 0,
          });
        }
      }
    } else if (!codeMap[`mat:${mat.id}`]) {
      getOrCreateGroup(null, mat);
    }
  }

  // Merge code groups that share the same material using Union-Find
  // e.g. codes 60645178 and 60645420 both mapping to the same material → one row
  const parent = {};
  const find = (x) => {
    if (parent[x] === undefined) parent[x] = x;
    if (parent[x] !== x) parent[x] = find(parent[x]);
    return parent[x];
  };
  const union = (x, y) => {
    const px = find(x), py = find(y);
    if (px !== py) parent[px] = py;
  };

  for (const key of Object.keys(codeMap)) find(key);

  // For each material, union all code groups that contain it
  const codeKeysByMaterial = {};
  for (const [key, group] of Object.entries(codeMap)) {
    if (key.startsWith("mat:")) continue;
    for (const matID of group.materialMap.keys()) {
      if (!codeKeysByMaterial[matID]) codeKeysByMaterial[matID] = [];
      codeKeysByMaterial[matID].push(key);
    }
  }
  for (const keys of Object.values(codeKeysByMaterial)) {
    for (let i = 1; i < keys.length; i++) union(keys[0], keys[i]);
  }

  // Build merged groups keyed by Union-Find root
  const mergedMap = {};
  for (const [key, group] of Object.entries(codeMap)) {
    const root = find(key);
    if (!mergedMap[root]) {
      mergedMap[root] = { codes: [], materialMap: new Map(), cpoDemand: 0, spoSupply: 0 };
    }
    const merged = mergedMap[root];
    if (group.code) merged.codes.push(group.code);
    merged.cpoDemand += group.cpoDemand;
    merged.spoSupply += group.spoSupply;
    for (const [matID, matEntry] of group.materialMap) {
      if (!merged.materialMap.has(matID)) {
        merged.materialMap.set(matID, { ...matEntry });
      } else {
        // Same material appears in multiple codes being merged — add demand/supply,
        // but keep inventory counted only once (it's the same physical stock)
        const existing = merged.materialMap.get(matID);
        existing.cpoDemand += matEntry.cpoDemand;
        existing.spoSupply += matEntry.spoSupply;
      }
    }
  }

  // Build final rows
  return Object.values(mergedMap)
    .map((group) => {
      const materials = [...group.materialMap.values()];

      const inventory = materials.reduce((sum, m) => sum + m.inventory, 0);
      const totalSupply = group.spoSupply + inventory;
      const balance = totalSupply - group.cpoDemand;

      const materialsWithBalance = materials.map((m) => {
        const matTotalSupply = m.spoSupply + m.inventory;
        const matBalance = matTotalSupply - m.cpoDemand;
        return { ...m, totalSupply: matTotalSupply, balance: matBalance };
      });

      return {
        code: group.codes.sort().join("/"),
        materials: materialsWithBalance,
        cpoDemand: group.cpoDemand,
        spoSupply: group.spoSupply,
        inventory,
        totalSupply,
        balance,
        status: balance > 0.01 ? "over" : balance < -0.01 ? "under" : "balanced",
      };
    })
    .sort((a, b) => a.balance - b.balance);
}
